import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import { List, ListItem } from 'chaoskit/src/components';

import BaseLayout from '../../layouts/BaseLayout';
import StaticFooter from '../../partials/footer/StaticFooter';
import Hero from '../../partials/Hero';
import { Data, Iframe } from '../../components';
import DemoQuote from '../../helpers/demo-quote.json';
import chiliPiper from '../../helpers/chili-piper';

const Demo1ColumnVariationLandingPage = ({
  data: {
    markdownRemark: {
      frontmatter: { seoTitle, hero, form },
    },
  },
}) => (
  <Fragment>
    <Hero compact contrast={false} particles={false}>
      <div className="hero-content">
        <div className="container">
          <div className="hero-titleWrapper">
            <h1 className="hero-title">
              <Data>{hero.heading}</Data>
            </h1>
          </div>
        </div>
      </div>
    </Hero>
    <section className="section">
      <div className="container">
        <div className="row u-flexCenter">
          <div className="column-6@medium">
            <Iframe src={form.embed} title={seoTitle} />
          </div>
        </div>
      </div>
    </section>
    <section className="section section--large u-bgPanel">
      <div className="container container--small">
        <blockquote className="quote u-textFluid--xlarge u-textHeading">
          <Data markdown>{DemoQuote.text}</Data>
        </blockquote>
        <div className="demo__quote-cite">
          <h5 className="u-textMuted">
            <Data>{DemoQuote.author.name}</Data>,{' '}
            <Data>{DemoQuote.author.title}</Data> at{' '}
            <Data>{DemoQuote.company.name}</Data>
          </h5>
          <img src={DemoQuote.company.logo} alt={DemoQuote.company.name} />
        </div>
      </div>
    </section>
  </Fragment>
);

Demo1ColumnVariationLandingPage.propTypes = {
  data: PropTypes.object.isRequired,
};

const Demo2ColumnVariationLandingPage = ({
  data: {
    markdownRemark: {
      frontmatter: { seoTitle, hero, section1, form },
    },
  },
}) => (
  <section className="demo2Column__content">
    <div className="container">
      <div className="row row--collapse demo2Column__row">
        <div className="column-6@medium demo2Column__column">
          <h3 className="demo2Column__heading">
            <Data>{hero.heading}</Data>
          </h3>
          {section1 && (
            <Fragment>
              {section1.blurb && (
                <div className="u-textMedium">
                  <Data markdown>{section1.blurb}</Data>
                </div>
              )}
              {section1.list.heading && (
                <h5 className="u-textTeal">
                  <Data>{section1.list.heading}</Data>
                </h5>
              )}
              {section1.list.items && (
                <List type={['space']} className="demo2Column__list">
                  {section1.list.items.map(({ blurb }) => (
                    <ListItem key={blurb}>
                      <Data>{blurb}</Data>
                    </ListItem>
                  ))}
                </List>
              )}
              {section1.closingBlurb && (
                <div className="u-textMedium">
                  <Data markdown>{section1.closingBlurb}</Data>
                </div>
              )}
            </Fragment>
          )}
        </div>
        <div className="column-6@medium demo2Column__column">
          <Iframe src={form.embed} title={seoTitle} />
          <blockquote className="quote u-textFluid--large u-textHeading u-mt--large">
            <Data markdown>{DemoQuote.text}</Data>
          </blockquote>
          <div className="demo__quote-cite">
            <h5 className="u-textMuted">
              <Data>{DemoQuote.author.name}</Data>,{' '}
              <Data>{DemoQuote.author.title}</Data> at{' '}
              <Data>{DemoQuote.company.name}</Data>
            </h5>
            <img src={DemoQuote.company.logo} alt={DemoQuote.company.name} />
          </div>
        </div>
      </div>
    </div>
  </section>
);

Demo2ColumnVariationLandingPage.propTypes = {
  data: PropTypes.object.isRequired,
};

const DemoColumnVariationLandingPagePicker = ({
  data,
  pageContext: { nodeType },
}) => {
  useEffect(() => {
    // Used to listen for data sent from iframe Pardot form
    window.addEventListener('message', chiliPiper);

    return () => {
      window.removeEventListener('message', chiliPiper);
    };
  }, []);

  return (
    <BaseLayout
      noFooter
      headerProps={{
        showPrimaryLogo: true,
        contrast: false,
        sticky: false,
        showNav: false,
      }}
    >
      <Helmet
        title={data.markdownRemark.frontmatter.seoTitle}
        meta={[
          {
            name: 'description',
            content: data.markdownRemark.frontmatter.description,
          },
          { name: 'robots', content: 'noindex, follow' },
          { name: 'node-type', content: nodeType },
        ]}
      >
        {/* Used in above scheduling form call */}
        <script src="https://js.chilipiper.com/marketing.js" />
      </Helmet>
      {parseInt(data.markdownRemark.frontmatter.columns, 10) === 2 ? (
        <Demo2ColumnVariationLandingPage data={data} />
      ) : (
        <Demo1ColumnVariationLandingPage data={data} />
      )}
      <StaticFooter />
    </BaseLayout>
  );
};

DemoColumnVariationLandingPagePicker.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
};

export default DemoColumnVariationLandingPagePicker;

export const pageQuery = graphql`
  query DemoColumnVariationLandingPageSingle($slug: String!) {
    markdownRemark(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/demo-column-variation-landing-pages/" }
    ) {
      fileAbsolutePath
      frontmatter {
        seoTitle
        description
        columns
        slug
        hero {
          heading
        }
        section1 {
          blurb
          list {
            heading
            items {
              blurb
            }
          }
          closingBlurb
        }
        form {
          embed
        }
      }
    }
  }
`;
